"use client";

import { useEffect, useRef, useState } from "react";

/* Carrinho Shoppub em demonstração contínua (dados reais da Shelter):
   1) o upsell com 30% off é adicionado ao carrinho;
   2) um cupom é digitado e aplicado — descontos e total recalculam.
   O ciclo reinicia sozinho. Card branco, mesma linguagem do checkout. */

const CDN = "https://cdn.shoppub.io/cdn-cgi/image";
const FOTO_MOCASSIM = `${CDN}/w=200,h=200,q=80,f=auto/useshelter/media/uploads/produtos/foto/ffsjuzwv/still-2_0009_preenchimento-generativo-2.jpg`;
const FOTO_PORTA = `${CDN}/w=200,h=200,q=80,f=auto/useshelter/media/uploads/produtos/foto/rfaapdez/acessorios_0006_camada-3.jpg`;

const CUPOM = "BEMVINDA10";

type Fase = "inicio" | "upsell" | "digitando" | "aplicado";

const fmt = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

export default function MockCarrinho() {
  const raiz = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  const [fase, setFase] = useState<Fase>("inicio");
  const [chars, setChars] = useState(0);

  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight * 0.95) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setOn(true), io.disconnect()),
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!on) return;
    let vivo = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(() => vivo && fn(), ms));
    const rodada = () => {
      setFase("inicio");
      setChars(0);
      t(() => setFase("upsell"), 2000);
      t(() => setFase("digitando"), 4200);
      for (let i = 1; i <= CUPOM.length; i++) t(() => setChars(i), 4400 + i * 110);
      t(() => setFase("aplicado"), 4400 + CUPOM.length * 110 + 600);
      t(rodada, 11200);
    };
    rodada();
    return () => {
      vivo = false;
      timers.forEach(clearTimeout);
    };
  }, [on]);

  const temUpsell = fase !== "inicio";
  const cupomOk = fase === "aplicado";
  const descontos = (temUpsell ? 90 : 0) + (cupomOk ? 87 : 0);
  const total = 660 + (temUpsell ? 210 : 0) - (cupomOk ? 87 : 0);

  return (
    <div ref={raiz} className="pointer-events-none select-none rounded-card bg-paper p-4 text-ink sm:p-5 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.7)]">
      <div className="flex items-center justify-between border-b border-line pb-3">
        <span className="text-sm font-semibold">Meu carrinho</span>
        <span className="text-sm text-smoke">{temUpsell ? "2 itens" : "1 item"}</span>
      </div>

      {/* barra de frete grátis */}
      <div className="mt-3">
        <div className="h-1.5 overflow-hidden rounded-full bg-mist">
          <div className="h-full rounded-full bg-lime-deep transition-all duration-700" style={{ width: on ? "100%" : "40%" }} />
        </div>
        <div className="mt-1.5 text-[11px] font-bold text-lime-deep">
          Parabéns, você ganhou frete grátis
        </div>
      </div>

      {/* itens + oferta: região de altura FIXA — o card não cresce
          quando o item entra, o scroll da página não se move */}
      <div className="flex h-[204px] flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b border-line py-3">
        <img src={FOTO_MOCASSIM} alt="Mocassim Blair Cow Print" loading="lazy" className="h-11 w-11 rounded-xl bg-mist object-cover" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">Mocassim Blair Cow Print</div>
          <div className="text-xs text-smoke">Tam 36 · 1 un</div>
        </div>
        <span className="shrink-0 text-sm font-semibold">{fmt(660)}</span>
      </div>

      {temUpsell && (
        <div className="flex animate-[reveal-up_0.5s_cubic-bezier(0.22,1,0.36,1)] items-center gap-3 border-b border-line py-3">
          <img src={FOTO_PORTA} alt="Porta Celular Shelter Íris" loading="lazy" className="h-11 w-11 rounded-xl bg-mist object-cover" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">Porta Celular Shelter Íris</div>
            <div className="truncate text-xs font-bold text-lime-deep">30% off aplicado</div>
          </div>
          <span className="flex shrink-0 flex-col items-end text-sm font-semibold leading-tight">
            <s className="text-xs font-normal text-smoke">{fmt(300)}</s>
            {fmt(210)}
          </span>
        </div>
      )}

      {/* upsell */}
      <div
        className={`mt-auto rounded-2xl p-4 transition-all duration-300 ${
          temUpsell ? "border border-lime-deep/50 bg-lime/15" : "bg-lime"
        }`}
      >
        {!temUpsell ? (
          <div className="flex items-center justify-between gap-3 text-ink">
            <div className="flex min-w-0 items-center gap-3">
              <img src={FOTO_PORTA} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-xl bg-paper object-cover" />
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink/60">
                  Aproveite e leve também · −30%
                </div>
                <div className="truncate text-sm font-bold">Porta Celular Shelter Íris</div>
                <div className="text-xs font-medium text-ink/70">
                  <s>{fmt(300)}</s> <b>{fmt(210)}</b>
                </div>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-ink px-4 py-2 text-xs font-bold text-lime">
              Adicionar
            </span>
          </div>
        ) : (
          <div className="whitespace-nowrap text-[12px] font-bold text-lime-deep sm:text-[13px]">
            ✓ Oferta adicionada <span className="sm:hidden">· −{fmt(90)}</span>
            <span className="hidden sm:inline">— você economizou {fmt(90)}</span>
          </div>
        )}
      </div>

      </div>

      {/* cupom (com digitação) e entrega */}
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {!cupomOk ? (
          <div
            className={`flex h-9 items-center overflow-hidden rounded-xl border text-[11px] transition-colors duration-300 ${
              fase === "digitando" ? "border-lime-deep/70" : "border-line"
            }`}
          >
            <span className="flex-1 truncate px-3 font-mono font-bold text-ink">
              {chars > 0 ? (
                <>
                  {CUPOM.slice(0, chars)}
                  <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-ink align-middle" />
                </>
              ) : (
                <span className="font-sans font-normal text-smoke">Insira o cupom</span>
              )}
            </span>
            <span
              className={`flex h-full items-center px-3 font-bold transition-all duration-200 ${
                fase === "digitando" && chars === CUPOM.length
                  ? "scale-95 bg-lime text-ink"
                  : "bg-mist text-ink"
              }`}
            >
              Usar
            </span>
          </div>
        ) : (
          <div className="flex h-9 animate-[reveal-up_0.4s_cubic-bezier(0.22,1,0.36,1)] items-center justify-between whitespace-nowrap rounded-xl border border-lime-deep/60 bg-lime/20 px-3 text-[11px] font-bold text-lime-deep">
            ✓ {CUPOM} · −10%
            <span className="font-mono">−{fmt(87)}</span>
          </div>
        )}
        <div className="flex h-9 items-center overflow-hidden rounded-xl border border-line text-[11px]">
          <span className="flex-1 truncate px-3 text-smoke">Digite o CEP</span>
          <span className="flex h-full items-center bg-ink px-3 font-bold text-paper">Calcular</span>
        </div>
      </div>

      {/* totais */}
      <div className="mt-3 flex flex-col gap-1 border-t border-line pt-3 text-[13px]">
        <div className="flex justify-between text-smoke">
          <span>Descontos</span>
          <span className={descontos > 0 ? "font-bold text-lime-deep" : ""}>
            {descontos > 0 ? `− ${fmt(descontos)}` : fmt(0)}
          </span>
        </div>
        <div className="flex justify-between font-display text-lg font-bold">
          <span>Total</span>
          <span>{fmt(total)}</span>
        </div>
      </div>
      <div className="mt-3 rounded-full bg-ink py-3 text-center text-sm font-bold text-lime">
        Finalizar compra
      </div>
    </div>
  );
}
